import React, { useEffect, useState } from 'react';
import {
  BankItemKind,
  FundReturn,
  UnexplainedBankItem,
} from '../clients/rust-api-client';
import { ledgerService } from '../clients/ApiClients';
import { Button } from '../common/Catalyst/button';
import { ErrorAlert } from '../common/Custom/ErrorAlert';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from '../common/Catalyst/dialog';
import {
  Description,
  Field,
  FieldGroup,
  Fieldset,
  Label,
} from '../common/Catalyst/fieldset';
import { FieldPicker } from '../common/Custom/FieldPicker';
import {
  billingErrorMessage,
  formatBillingDate,
  formatMoney,
} from '../utils/billingHelpers';

interface Props {
  hoaId: string;
  reconciliationId: string;
  item: UnexplainedBankItem;
  onClose: () => void;
  onPosted: () => void;
}

/**
 * A closed set of three. Anything else on a statement either settles a
 * posting we already made — and is matched, not posted — or needs a
 * person to decide what it is, which is what a manual journal entry is
 * for. There is deliberately no "payment" here: that is what keeps a
 * homeowner's cheque from being counted twice.
 */
const KINDS: { value: BankItemKind; label: string }[] = [
  { value: 'interest', label: 'Bank interest' },
  { value: 'reserve_interest', label: 'Reserve interest' },
  { value: 'service_charge', label: 'Service charge' },
];

/**
 * Post an item the institution originated, and clear it in the same
 * call.
 *
 * The bank feed is evidence, not a posting source — except where the
 * bank *originates* the money rather than settling something we already
 * recorded. Double-counting needs two sources; a cheque has our payment
 * record and the bank's deposit line, while interest has only the bank.
 */
const PostBankItemDialog: React.FC<Props> = ({
  hoaId,
  reconciliationId,
  item,
  onClose,
  onPosted,
}) => {
  // The institution's own wording, read as a hint. It selects the
  // dropdown and decides nothing: the strings vary by processor, there
  // is no corpus of real ones to calibrate against, and a journal entry
  // is not reversible.
  const [kind, setKind] = useState<BankItemKind>(item.looks_like ?? 'interest');
  const [funds, setFunds] = useState<FundReturn[]>([]);
  const [fundId, setFundId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await ledgerService.listFunds({ hoaId });
        const active = data.funds.filter((f) => f.is_active);
        setFunds(active);
        setFundId((current) => current || active[0]?.id || '');
      } catch (err) {
        setError(billingErrorMessage(err, "Couldn't load the funds."));
      }
    };
    void load();
  }, [hoaId]);

  const submit = async () => {
    try {
      setSaving(true);
      setError(null);
      await ledgerService.postBankItem({
        hoaId,
        reconciliationId,
        bankItemPost: {
          bank_transaction_id: item.bank_transaction_id,
          kind,
          fund_id: fundId,
        },
      });
      onPosted();
    } catch (err) {
      setError(billingErrorMessage(err, "Couldn't post this item."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose}>
      <DialogTitle>Post this to the books</DialogTitle>
      <DialogDescription>
        {formatBillingDate(item.posted_on)} ·{' '}
        {item.description ?? 'No description'} ·{' '}
        <span className="tabular-nums">{formatMoney(item.amount)}</span>
      </DialogDescription>
      <DialogBody>
        <Fieldset>
          <FieldGroup>
            <Field>
              <Label>What is it?</Label>
              <FieldPicker
                label="What is it?"
                value={kind}
                onChange={(v) => setKind(v as BankItemKind)}
                options={KINDS}
              />
              {item.looks_like && (
                <Description>
                  The institution's wording suggests this. Check it — the
                  suggestion is a guess at a vendor string and nothing posts on
                  its own.
                </Description>
              )}
            </Field>
            <Field>
              <Label>Fund</Label>
              <FieldPicker
                label="Fund"
                value={fundId}
                onChange={setFundId}
                options={funds.map((f) => ({ value: f.id, label: f.name }))}
              />
              {/*
                Not derivable from the account. Interest is paid on
                operating, reserve and other accounts alike, so which
                fund it belongs to cannot be inferred from the cash
                account that earned it.
              */}
              <Description>
                Which fund the income or expense belongs to.
              </Description>
            </Field>
          </FieldGroup>
        </Fieldset>

        <p className="mt-4 text-sm text-zinc-500 dark:text-zinc-400">
          This writes a journal entry and clears it against this transaction in
          the same step — left uncleared it would become a deposit in transit
          and the statement would stop balancing.
        </p>

        <ErrorAlert className="mt-4">{error}</ErrorAlert>
      </DialogBody>
      <DialogActions>
        <Button plain onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button color="sky" onClick={submit} disabled={saving || !fundId}>
          {saving ? 'Posting…' : 'Post and clear'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PostBankItemDialog;
